import { create } from 'zustand';
import { TransferType, OrderStatus } from '@/types/enums';
import { useAccountStore } from './use-account-store';

export type NewTransfer = {
  id: string;
  size: bigint;
  orderType: TransferType;
  status: OrderStatus.NEW;
  chainId: number;
  postTime: number;
  isSolanaNative: boolean;
};

export type FilledTransfer = {
  id: string;
  size: bigint;
  orderType: TransferType;
  status: OrderStatus.FILLED;
  chainId: number;
  postTime: number;
  isSolanaNative: boolean;
};

export type RejectedTransfer = {
  id: string;
  orderType: TransferType;
  status: OrderStatus.REJECTED;
  code: number;
  chainId: number;
  postTime: number;
  isSolanaNative: boolean;
};

export type VerifiedTransfer = {
  id: string;
  orderType: TransferType;
  status: OrderStatus.VERIFIED;
  size: bigint;
  chainId: number;
  postTime: number;
  requestArgs: string;
  signer: string;
  signature: string;
  signatureProof: string;
  validatorSignature: string;
  isSolanaNative: boolean;
};

export type Transfer =
  | NewTransfer
  | FilledTransfer
  | RejectedTransfer
  | VerifiedTransfer;

interface TransfersStore {
  newDeposits: Record<string, Transfer[]>;
  addNewDeposit: (transfer: Transfer) => void;
  removeNewDeposit: (id: string) => void;

  transfers: Record<string, Record<string, Transfer>>;
  setTransfer: (id: string, transfer: Transfer) => void;
  clearTransfersStore: () => void;
}

export const useTransfersStore = create<TransfersStore>((set, get) => ({
  newDeposits: {},
  addNewDeposit: (transfer: Transfer) => {
    const address = useAccountStore.getState().address || '';

    set((state) => ({
      newDeposits: {
        ...state.newDeposits,
        [address]: [...(state.newDeposits[address] ?? []), transfer],
      },
    }));
  },
  removeNewDeposit: (id: string) => {
    const address = useAccountStore.getState().address || '';

    set((state) => ({
      newDeposits: {
        ...state.newDeposits,
        [address]: state.newDeposits[address].filter((t) => t.id !== id),
      },
    }));
  },
  transfers: {},
  setTransfer: (id: string, transfer: Transfer) => {
    const address = useAccountStore.getState().address || '';

    set((state) => ({
      transfers: {
        ...state.transfers,
        [address]: { ...(state.transfers[address] ?? []), [id]: transfer },
      },
    }));
  },
  clearTransfersStore: () => {
    set({
      newDeposits: {},
      transfers: {},
    });
  },
}));
